'use client'

import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ModelForm } from '@/components/partials/models'
import { useUpdateModel, useModel } from '@/hooks'
import { UpdateModelParams } from '@/types/model'
import { ContentError, FormSkeleton } from '@/components/partials'
import { FormModeEnum } from '@/enums'
import { useState } from 'react'

type PropsType = {
  id: string
}

const UpdateModel = ({ id }: PropsType) => {
  const router = useRouter()
  const t = useTranslations()
  const queryClient = useQueryClient()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: modelData, isLoading, error } = useModel(id)
  const { mutate: updateModelMutate, isPending } = useUpdateModel()

  const handleSubmit = async (data: UpdateModelParams) => {
    updateModelMutate(
      { modelId: id, params: data },
      {
        onSuccess: async updatedModel => {
          setIsRedirecting(true)
          queryClient.setQueryData(['model', id], updatedModel)
          await queryClient.invalidateQueries({ queryKey: ['models'] })
          toast.success(t('updated_successfully'))
          router.push(`/products/models/${id}`)
        },
        onError: error => {
          console.error('Error updating model:', error)
          toast.error(t('error_updating'))
        }
      }
    )
  }

  const handleCancel = () => {
    router.push(`/products/models/${id}`)
  }

  if (isLoading) {
    return <FormSkeleton fieldRows={1} showTranslations={false} />
  }

  if (error || !modelData) {
    return <ContentError error={error} listUrl="/products/models" />
  }

  return (
    <div className="space-y-6">
      <ModelForm
        mode={FormModeEnum.UPDATE}
        initialData={modelData}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isPending || isRedirecting}
      />
    </div>
  )
}

export default UpdateModel
